/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Emitter, Event } from 'vs/base/common/event';

export interface IIconRegistry {
	readonly all: IterableIterator<Codicon>;
	readonly onDidRegister: Event<Codicon>;
	get(id: string): Codicon | undefined;
}

class Registry implements IIconRegistry {

	private readonly _icons = new Map<string, Codicon>();
	private readonly _onDidRegister = new Emitter<Codicon>();

	public add(icon: Codicon) {
		const existing = this._icons.get(icon.id);
		if (!existing) {
			this._icons.set(icon.id, icon);
			this._onDidRegister.fire(icon);
		} else if (icon.description) {
			existing.description = icon.description;
		} else {
			console.error(`Duplicate registration of codicon ${icon.id}`);
		}
	}

	public get(id: string): Codicon | undefined {
		return this._icons.get(id);
	}

	public get all(): IterableIterator<Codicon> {
		return this._icons.values();
	}

	public get onDidRegister(): Event<Codicon> {
		return this._onDidRegister.event;
	}
}

const _registry = new Registry();

export const iconRegistry: IIconRegistry = _registry;

// Selects all codicon names encapsulated in the `$()` syntax and wraps the
// results with spaces so that screen readers can read the text better.
export function getCodiconAriaLabel(text: string | undefined) {
	if (!text) {
		return '';
	}

	return text.replace(/\$\((.*?)\)/g, (_match, codiconName) => ` ${codiconName} `).trim();
}

/**
 * The Codicon library is a set of default icons that are built-in in VS Code.
 *
 * In the product (outside of base) Codicons should only be used as defaults. In order to have all icons in VS Code
 * themeable, component should ise define new, component specific icons using `iconRegistry.registerIcon`.
 * In that call a Codicon can be names as default.
 */
export class Codicon implements CSSIcon {
	private constructor(public readonly id: string, public readonly definition: IconDefinition, public description?: string) {
		_registry.add(this);
	}
	public get classNames() { return 'codicon codicon-' + this.id; }
	// classNamesArray is useful for migrating to ES6 classlist
	public get classNamesArray() { return ['codicon', 'codicon-' + this.id]; }
	public get cssSelector() { return '.codicon.codicon-' + this.id; }


	// built-in icons, with image name
	public static readonly add = new Codicon('add', { fontCharacter: '\\ea60' });
	public static readonly lightBulb = new Codicon('light-bulb', { fontCharacter: '\\ea61' });
	public static readonly warning = new Codicon('warning', { fontCharacter: '\\ea6c' });
	public static readonly info = new Codicon('info', { fontCharacter: '\\ea74' });
	public static readonly close = new Codicon('close', { fontCharacter: '\\ea76' });
	public static readonly sync = new Codicon('sync', { fontCharacter: '\\ea77' });
	public static readonly symbolFolder = new Codicon('symbol-folder', { fontCharacter: '\\ea83' });
	public static readonly symbolEvent = new Codicon('symbol-event', { fontCharacter: '\\ea86' });
	public static readonly error = new Codicon('error', { fontCharacter: '\\ea87' });
	public static readonly symbolVariable = new Codicon('symbol-variable', { fontCharacter: '\\ea88' });
	public static readonly symbolArray = new Codicon('symbol-array', { fontCharacter: '\\ea8a' });
	public static readonly symbolModule = new Codicon('symbol-module', { fontCharacter: '\\ea8b' });
	public static readonly symbolPackage = new Codicon('symbol-package', { fontCharacter: '\\ea8b' });
	public static readonly symbolNamespace = new Codicon('symbol-namespace', { fontCharacter: '\\ea8b' });
	public static readonly symbolObject = new Codicon('symbol-object', { fontCharacter: '\\ea8b' });
	public static readonly symbolMethod = new Codicon('symbol-method', { fontCharacter: '\\ea8c' });
	public static readonly symbolFunction = new Codicon('symbol-function', { fontCharacter: '\\ea8c' });
	public static readonly symbolConstructor = new Codicon('symbol-constructor', { fontCharacter: '\\ea8c' });
	public static readonly symbolBoolean = new Codicon('symbol-boolean', { fontCharacter: '\\ea8f' });
	public static readonly symbolNull = new Codicon('symbol-null', { fontCharacter: '\\ea8f' });
	public static readonly symbolNumber = new Codicon('symbol-number', { fontCharacter: '\\ea90' });
	public static readonly symbolStruct = new Codicon('symbol-struct', { fontCharacter: '\\ea91' });
	public static readonly symbolTypeParameter = new Codicon('symbol-type-parameter', { fontCharacter: '\\ea92' });
	public static readonly symbolKey = new Codicon('symbol-key', { fontCharacter: '\\ea93' });
	public static readonly symbolText = new Codicon('symbol-text', { fontCharacter: '\\ea93' });
	public static readonly symbolReference = new Codicon('symbol-reference', { fontCharacter: '\\ea94' });
	public static readonly symbolEnum = new Codicon('symbol-enum', { fontCharacter: '\\ea95' });
	public static readonly symbolValue = new Codicon('symbol-value', { fontCharacter: '\\ea95' });
	public static readonly symbolUnit = new Codicon('symbol-unit', { fontCharacter: '\\ea96' });
	public static readonly arrowDown = new Codicon('arrow-down', { fontCharacter: '\\ea9a' });
	public static readonly arrowLeft = new Codicon('arrow-left', { fontCharacter: '\\ea9b' });
	public static readonly arrowUp = new Codicon('arrow-up', { fontCharacter: '\\eaa1' });
	public static readonly caseSensitive = new Codicon('case-sensitive', { fontCharacter: '\\eab1' });
	public static readonly check = new Codicon('check', { fontCharacter: '\\eab2' });
	public static readonly chevronDown = new Codicon('chevron-down', { fontCharacter: '\\eab4' });
	public static readonly chevronRight = new Codicon('chevron-right', { fontCharacter: '\\eab6' });
	public static readonly chevronUp = new Codicon('chevron-up', { fontCharacter: '\\eab7' });
	public static readonly lightbulbAutofix = new Codicon('lightbulb-autofix', { fontCharacter: '\\eb13' });
	public static readonly loading = new Codicon('loading', { fontCharacter: '\\eb19' });
	public static readonly preserveCase = new Codicon('preserve-case', { fontCharacter: '\\eb2e' });
	public static readonly regex = new Codicon('regex', { fontCharacter: '\\eb38' });
	public static readonly remove = new Codicon('remove', { fontCharacter: '\\eb3b' });
	public static readonly replaceAll = new Codicon('replace-all', { fontCharacter: '\\eb3c' });
	public static readonly replace = new Codicon('replace', { fontCharacter: '\\eb3d' });
	public static readonly splitHorizontal = new Codicon('split-horizontal', { fontCharacter: '\\eb56' });
	public static readonly splitVertical = new Codicon('split-vertical', { fontCharacter: '\\eb57' });
	public static readonly symbolClass = new Codicon('symbol-class', { fontCharacter: '\\eb5b' });
	public static readonly symbolColor = new Codicon('symbol-color', { fontCharacter: '\\eb5c' });
	public static readonly symbolConstant = new Codicon('symbol-constant', { fontCharacter: '\\eb5d' });
	public static readonly symbolEnumMember = new Codicon('symbol-enum-member', { fontCharacter: '\\eb5e' });
	public static readonly symbolField = new Codicon('symbol-field', { fontCharacter: '\\eb5f' });
	public static readonly symbolFile = new Codicon('symbol-file', { fontCharacter: '\\eb60' });
	public static readonly symbolInterface = new Codicon('symbol-interface', { fontCharacter: '\\eb61' });
	public static readonly symbolKeyword = new Codicon('symbol-keyword', { fontCharacter: '\\eb62' });
	public static readonly symbolOperator = new Codicon('symbol-operator', { fontCharacter: '\\eb64' });
	public static readonly symbolProperty = new Codicon('symbol-property', { fontCharacter: '\\eb65' });
	public static readonly symbolSnippet = new Codicon('symbol-snippet', { fontCharacter: '\\eb66' });
	public static readonly triangleDown = new Codicon('triangle-down', { fontCharacter: '\\eb6e' });
	public static readonly triangleLeft = new Codicon('triangle-left', { fontCharacter: '\\eb6f' });
	public static readonly triangleRight = new Codicon('triangle-right', { fontCharacter: '\\eb70' });
	public static readonly triangleUp = new Codicon('triangle-up', { fontCharacter: '\\eb71' });
	public static readonly wholeWord = new Codicon('whole-word', { fontCharacter: '\\eb7e' });
	public static readonly listFilter = new Codicon('list-filter', { fontCharacter: '\\eb83' });
	public static readonly listSelection = new Codicon('list-selection', { fontCharacter: '\\eb85' });
	public static readonly selection = new Codicon('selection', { fontCharacter: '\\eb85' });
	public static readonly symbolString = new Codicon('symbol-string', { fontCharacter: '\\eb8d' });

	public static readonly treeItemExpanded = new Codicon('tree-item-expanded', Codicon.chevronDown.definition); // collapsed is done with rotation

	public static readonly treeFilterOnTypeOn = new Codicon('tree-filter-on-type-on', Codicon.listFilter.definition);
	public static readonly treeFilterOnTypeOff = new Codicon('tree-filter-on-type-off', Codicon.listSelection.definition);
	public static readonly treeFilterClear = new Codicon('tree-filter-clear', Codicon.close.definition);

	public static readonly treeItemLoading = new Codicon('tree-item-loading', Codicon.loading.definition);

	public static readonly menuSelection = new Codicon('menu-selection', Codicon.check.definition);
	public static readonly menuSubmenu = new Codicon('menu-submenu', Codicon.chevronRight.definition);

	public static readonly scrollbarButtonLeft = new Codicon('scrollbar-button-left', Codicon.triangleLeft.definition);
	public static readonly scrollbarButtonRight = new Codicon('scrollbar-button-right', Codicon.triangleRight.definition);

	public static readonly scrollbarButtonUp = new Codicon('scrollbar-button-up', Codicon.triangleUp.definition);
	public static readonly scrollbarButtonDown = new Codicon('scrollbar-button-down', Codicon.triangleDown.definition);

	public static readonly quickInputBack = new Codicon('quick-input-back', Codicon.arrowLeft.definition);
}

export interface CSSIcon {
	readonly id: string;
}


export namespace CSSIcon {
	export const iconNameSegment = '[A-Za-z0-9]+';
	export const iconNameExpression = '[A-Za-z0-9-]+';
	export const iconModifierExpression = '~[A-Za-z]+';
	export const iconNameCharacter = '[A-Za-z0-9~-]';

	const cssIconIdRegex = new RegExp(`^(${iconNameExpression})(${iconModifierExpression})?$`);

	export function asClassNameArray(icon: CSSIcon): string[] {
		if (icon instanceof Codicon) {
			return ['codicon', 'codicon-' + icon.id];
		}
		const match = cssIconIdRegex.exec(icon.id);
		if (!match) {
			return asClassNameArray(Codicon.error);
		}
		let [, id, modifier] = match;
		const classNames = ['codicon', 'codicon-' + id];
		if (modifier) {
			classNames.push('codicon-modifier-' + modifier.substr(1));
		}
		return classNames;
	}

	export function asClassName(icon: CSSIcon): string {
		return asClassNameArray(icon).join(' ');
	}

	export function asCSSSelector(icon: CSSIcon): string {
		return '.' + asClassNameArray(icon).join('.');
	}
}


interface IconDefinition {
	fontCharacter: string;
}
